import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
} from "@mui/material";
import {
  CustomDialogProps,
  SmartForm,
  SmartSubmit,
  SmartTextField,
  useSmartForm,
} from "@nodeon/react-components";
import { useSnackbar } from "notistack";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiResponse,
  CoordinateSystemListDto,
  useCoordinateSystemApi,
} from "~/api/GeneratedApi";

interface FormProps {
  name: string;
  description: string;
}

interface Props {
  coordinateSystem?: CoordinateSystemListDto;
  onSubmitted?: (name: string) => void;
  initialValue?: string;
}

const CoordinateSystemDialog = ({
  handleClose,
  onSubmitted,
  coordinateSystem,
  initialValue,
}: CustomDialogProps & Props): JSX.Element => {
  const { editCoordinateSystem, addCoordinateSystem } =
    useCoordinateSystemApi();
  const { t } = useTranslation("coordinate-system");
  const { enqueueSnackbar } = useSnackbar();

  const edit = useMemo(() => !!coordinateSystem, [coordinateSystem]);

  const onSuccess = (data: FormProps, res: ApiResponse) => {
    enqueueSnackbar(t(`apiResponses:${res.resultKey}`), { variant: "success" });
    onSubmitted?.(data.name);
    handleClose?.();
  };

  const onSubmit = (data: FormProps) => {
    return edit
      ? editCoordinateSystem(
          {
            name: data.name,
            description: data.description,
          },
          coordinateSystem!.id,
        )
      : addCoordinateSystem({
          name: data.name,
          description: data.description,
        });
  };

  const defaultValues = useMemo(
    () => ({
      name: coordinateSystem?.name ?? initialValue ?? "",
      description: coordinateSystem?.description ?? "",
    }),
    [coordinateSystem?.description, coordinateSystem?.name, initialValue],
  );

  const formMethods = useSmartForm<FormProps, ApiResponse>({
    defaultValues,
  });

  return (
    <Dialog open onClose={handleClose}>
      <SmartForm<FormProps, ApiResponse>
        onSubmit={onSubmit}
        noGrid
        onSuccess={(data, res) => onSuccess(data, res)}
        methods={formMethods}
      >
        <DialogTitle id="form-dialog-title">
          {edit ? t("edit-title") : t("create-title")}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SmartTextField<FormProps>
                fullWidth
                name="name"
                label={t("name")}
                required
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">EPSG:</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <SmartTextField<FormProps>
                fullWidth
                name="description"
                label={t("description")}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {t("general:cancel")}
          </Button>
          <SmartSubmit disabled={edit ? !formMethods.formState.isDirty : false}>
            {edit ? t("general:edit") : t("general:create")}
          </SmartSubmit>
        </DialogActions>
      </SmartForm>
    </Dialog>
  );
};

export default CoordinateSystemDialog;
